import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { storageService } from '../../../services/storageService';
import { supabase } from '../../../lib/supabase';

const ImageUploadSection = ({ images, onImagesChange, error }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const mockImages = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1618857320903-6e7eea312a8e",
    alt: "Person paddleboarding on calm turquoise water during golden hour sunset with mountains in background"
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1643149949871-e20f4673dc76",
    alt: "Group of paddleboarders in colorful swimwear standing on boards in clear blue ocean water"
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1500524869404-ca3811194ffa",
    alt: "Aerial view of single paddleboarder on transparent blue water with visible sandy bottom"
  }];


  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);

    // Validate files
    const validFiles = files?.filter((file) => {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']?.includes(file?.type);
      const isValidSize = file?.size <= 10485760; // 10MB

      if (!isValidType) {
        alert(`${file?.name}: Sadece JPG, PNG, WEBP formatları desteklenir`);
        return false;
      }
      if (!isValidSize) {
        alert(`${file?.name}: Maksimum dosya boyutu 10MB`);
        return false;
      }
      return true;
    });

    if (validFiles?.length === 0) return;

    setUploading(true);
    setUploadProgress(`0 / ${validFiles?.length} görsel yükleniyor...`);

    try {
      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        throw new Error('Görsel yüklemek için giriş yapmalısınız');
      }

      // Upload each file and get public URLs
      const uploadedImages = [];
      for (let i = 0; i < validFiles?.length; i++) {
        const file = validFiles?.[i];
        setUploadProgress(`${i + 1} / ${validFiles?.length} görsel yükleniyor...`);

        const publicUrl = await storageService?.uploadEventImage(file, user?.id);

        uploadedImages?.push({
          id: Date.now() + i,
          url: publicUrl,
          alt: `Etkinlik görseli ${images?.length + i + 1}`,
          file: null // No need to store file object anymore
        });
      }

      // Add uploaded images to existing images
      onImagesChange([...images, ...uploadedImages]);

      setUploadProgress(null);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(`Görsel yükleme hatası: ${error?.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (id) => {
    const imageToRemove = images?.find((img) => img?.id === id);

    // If image is from Supabase Storage (not a mock image), delete it
    if (imageToRemove && !imageToRemove?.isMock && imageToRemove?.url?.includes('event-images')) {
      try {
        await storageService?.deleteEventImage(imageToRemove?.url);
      } catch (error) {
        console.error('Error deleting image from storage:', error);
        // Continue with removal from UI even if deletion fails
      }
    }

    onImagesChange(images?.filter((img) => img?.id !== id));
  };

  const handleUseMockImage = (mockImg) => {
    const newImage = {
      id: Date.now(),
      url: mockImg?.url,
      alt: mockImg?.alt,
      isMock: true
    };
    onImagesChange([...images, newImage]);
  };

  return (
    <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-4 md:p-6 lg:p-8 shadow-organic">
      <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-5 lg:mb-6">
        Etkinlik Görselleri
      </h2>
      <div
        className={`border-2 border-dashed rounded-lg md:rounded-xl p-6 md:p-8 lg:p-12 text-center transition-smooth ${
        dragActive ? 'border-primary bg-primary/5' : 'border-border'}`
        }
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}>

        <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-base md:text-lg font-medium text-foreground mb-2">
          Görselleri Sürükle & Bırak
        </h3>
        <p className="text-sm md:text-base text-muted-foreground mb-4">
          veya dosya seçmek için tıklayın
        </p>
        <input
          type="file"
          id="file-upload"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInput}
          disabled={uploading}
          className="hidden" />

        <label
          htmlFor="file-upload"
          className={`inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium rounded-lg transition-smooth ${
          uploading ?
          'bg-muted text-muted-foreground cursor-not-allowed' :
          'text-primary-foreground bg-primary hover:bg-primary/90 cursor-pointer'}`
          }>

          <Icon name="Image" size={18} />
          {uploading ? 'Yükleniyor...' : 'Görsel Seç'}
        </label>
        <p className="mt-3 text-xs md:text-sm text-muted-foreground">
          PNG, JPG, WEBP (Maks. 10MB)
        </p>
        
        {uploadProgress &&
        <p className="mt-3 text-sm text-primary font-medium">
            {uploadProgress}
          </p>
        }
      </div>
      {error &&
      <p className="mt-2 text-sm text-error">{error}</p>
      }
      {images?.length > 0 &&
      <div className="mt-6">
          <h3 className="text-base md:text-lg font-medium text-foreground mb-3 md:mb-4">
            Yüklenen Görseller ({images?.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {images?.map((img) =>
          <div key={img?.id} className="relative group">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <Image
                src={img?.url}
                alt={img?.alt}
                className="w-full h-full object-cover" />

                </div>
                <button
              type="button"
              onClick={() => handleRemoveImage(img?.id)}
              disabled={uploading}
              className="absolute top-2 right-2 p-1.5 bg-error text-error-foreground rounded-full opacity-0 group-hover:opacity-100 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed">

                  <Icon name="X" size={16} />
                </button>
                {!img?.isMock && img?.url?.includes('event-images') &&
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-success/90 text-success-foreground text-xs rounded">
                    Yüklendi
                  </div>
            }
              </div>
          )}
          </div>
        </div>
      }
      <div className="mt-6">
        <h3 className="text-base md:text-lg font-medium text-foreground mb-3 md:mb-4">
          Örnek Görseller Kullan
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {mockImages?.map((mockImg) =>
          <button
            key={mockImg?.id}
            type="button"
            onClick={() => handleUseMockImage(mockImg)}
            disabled={uploading}
            className="relative group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">

              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                <Image
                src={mockImg?.url}
                alt={mockImg?.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-smooth" />

              </div>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-smooth rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-medium">Kullan</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>);


};

export default ImageUploadSection;